import React from 'react';
import { AgentInvoiceAnalysis } from '../../../types/organizations';

interface AnalysesModalProps {
  isOpen: boolean;
  analyses: { type: string; analyses: AgentInvoiceAnalysis[] } | null;
  onClose: () => void;
}

const AnalysesModal: React.FC<AnalysesModalProps> = ({
  isOpen,
  analyses,
  onClose
}) => {
  if (!isOpen || !analyses) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-gray-900">{analyses.type}</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="space-y-4">
            <div className="max-h-96 overflow-y-auto space-y-4">
              {analyses.analyses.map((analysis, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg border-l-4 border-blue-500">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-900">
                      #{index + 1}
                    </span>
                    <span className="text-xs text-gray-500">
                      {new Date(analysis.analyzed_at).toLocaleString()}
                    </span>
                  </div>

                  {/* Show all details from output_json dynamically */}
                  {analysis.output_json && typeof analysis.output_json === 'object' && (
                    <div className="space-y-2 mb-3">
                      {Object.entries(analysis.output_json)
                        .filter(([key, value]) => value && typeof value === 'string' && key !== 'channel')
                        .map(([key, value]) => (
                          <div key={key} className="bg-white p-2 rounded border">
                            <span className="text-xs font-medium text-gray-600">
                              {key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}:
                            </span>
                            <div className="text-sm text-gray-800 whitespace-pre-line">
                              {value}
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                  
                  {analysis.reasonings && analysis.reasonings.length > 0 && (
                    <div className="space-y-1">
                      <span className="text-xs font-medium text-gray-600">Reasoning:</span>
                      {analysis.reasonings.map((reasoning, idx) => {
                        // Handle both string and object reasonings
                        if (typeof reasoning === 'string') {
                          return (
                            <div key={idx} className="text-xs text-gray-600 bg-white p-2 rounded border">
                              {reasoning}
                            </div>
                          );
                        } else if (typeof reasoning === 'object' && reasoning !== null) {
                          // Handle object reasonings by rendering each key-value pair
                          return (
                            <div key={idx} className="text-xs text-gray-600 bg-white p-2 rounded border">
                              {Object.entries(reasoning).map(([key, value]) => (
                                <div key={key} className="mb-1 last:mb-0">
                                  <span className="font-medium text-gray-800">{key}:</span>
                                  <div className="text-gray-600 ml-1">
                                    {typeof value === 'object' && value !== null ? (
                                      // Handle nested objects (like criteria)
                                      <div className="ml-1">
                                        {Object.entries(value).map(([nestedKey, nestedValue]) => (
                                          <div key={nestedKey} className="mb-1">
                                            <span className="font-medium text-gray-700">{nestedKey}:</span>
                                            <span className="ml-1">{String(nestedValue)}</span>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <span>{String(value)}</span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          );
                        } else {
                          // Fallback for any other type
                          return (
                            <div key={idx} className="text-xs text-gray-600 bg-white p-2 rounded border">
                              {String(reasoning)}
                            </div>
                          );
                        }
                      })}
                    </div>
                  )}
                  
                  {analysis.crew_run_id && (
                    <div className="mt-2 text-xs text-gray-500">
                      Crew Run: {analysis.crew_run_id}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalysesModal;
